import "./Translator.css";
import { renderCommonPhrase } from "./CommonPhrase";
import { renderPeopleAlsoTranslate } from "./PeopleAlsoTranslate";
import { renderAyeSearchTranslateOfficialResult } from "./AyeSearchTranslateOfficialResult";

const API_ROOT = "https://api.ayesearch.com/api/translations";

interface Language {
  code: string;
  name: string;
  native_name?: string;
  dir?: string;
}

interface TranslationResult {
  source_language: string;
  source_language_name: string;
  target_language: string;
  target_language_name: string;
  source_text: string;
  original_source_text?: string;
  canonical_source_text?: string;
  translation: string;
  method: string;
  confidence: number;
  verified: boolean;
  detected?: boolean;
  symbols_applied?: boolean;
}

interface ParsedQuery {
  matched?: boolean;
  text?: string;
  source?: string;
  target?: string;
  result?: TranslationResult | null;
}

function escapeHtml(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const FALLBACK_LANGUAGES: Language[] = [
  {code:"auto",name:"Auto-detect",native_name:"Auto-detect",dir:"ltr"},
  {code:"yo",name:"Yoruba",native_name:"Yorùbá",dir:"ltr"},
  {code:"ha",name:"Hausa",native_name:"Hausa",dir:"ltr"},
  {code:"ig",name:"Igbo",native_name:"Igbo",dir:"ltr"},
  {code:"en",name:"English",native_name:"English",dir:"ltr"},
  {code:"sw",name:"Swahili",native_name:"Kiswahili",dir:"ltr"},
];

const LANGUAGE_DISPLAY_PRIORITY: Record<string, number> = {
  yo: 0,
  ha: 1,
  ig: 2,
  en: 3,
  sw: 4,
};

function sortLanguages(languages: Language[]): Language[] {
  const auto = languages.filter((language) => language.code === "auto");
  const real = languages
    .filter((language) => language.code !== "auto")
    .sort(
      (a, b) =>
        (LANGUAGE_DISPLAY_PRIORITY[a.code] ?? 999) -
          (LANGUAGE_DISPLAY_PRIORITY[b.code] ?? 999) ||
        a.name.localeCompare(b.name),
    );

  return [...auto, ...real];
}

function options(
  languages: Language[],
  selected: string,
  allowAuto: boolean,
): string {
  return sortLanguages(languages)
    .filter((language) => allowAuto || language.code !== "auto")
    .map(
      (language) =>
        `<option value="${escapeHtml(language.code)}" ${
          language.code === selected ? "selected" : ""
        }>${escapeHtml(language.name)}</option>`,
    )
    .join("");
}

async function json<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: {Accept:"application/json"},
  });
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    throw new Error(`Translator API returned HTTP ${res.status}`);
  }
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error || `Translator API returned HTTP ${res.status}`);
  }
  return data as T;
}

async function loadLanguages(): Promise<Language[]> {
  try {
    const data = await json<{languages:Language[]}>(`${API_ROOT}/languages/`);
    return sortLanguages(data.languages?.length ? data.languages : FALLBACK_LANGUAGES);
  } catch {
    return sortLanguages(FALLBACK_LANGUAGES);
  }
}

async function parseQuery(query: string): Promise<ParsedQuery> {
  try {
    const url = new URL(`${API_ROOT}/translator-query/`);
    url.searchParams.set("q", query);
    return await json<ParsedQuery>(url.toString());
  } catch {
    return {matched:true,text:"",source:"auto",target:"en"};
  }
}

async function translate(
  text: string,
  source: string,
  target: string,
  symbols: boolean,
): Promise<TranslationResult> {
  const url = new URL(`${API_ROOT}/translate/`);
  url.searchParams.set("text", text);
  url.searchParams.set("source", source);
  url.searchParams.set("target", target);
  url.searchParams.set("symbols", symbols ? "1" : "0");
  return json<TranslationResult>(url.toString());
}

function resultHtml(result: TranslationResult | null): string {
  if (!result) {
    return `<div class="aye-translator-placeholder">Translation</div>`;
  }

  const note =
    result.method === "verified_phrase"
      ? "Verified common phrase"
      : result.method === "dictionary_concept"
        ? "AyeSearch dictionary match"
        : "Starter word-sequence translation";

  return `
    <div class="aye-translator-result">${escapeHtml(result.translation)}</div>
    <div class="aye-translator-method">${escapeHtml(note)}</div>
  `;
}

function detectedLabel(result: TranslationResult | null, source: string): string {
  if (source === "auto" && result?.source_language_name) {
    return `${result.source_language_name} · detected`;
  }
  return source === "auto" ? "Auto detect" : "Manual language";
}

export interface TranslatorRenderOptions {
  embedded?: boolean;
  includeExtras?: boolean;
}

export async function renderTranslator(
  container: HTMLElement,
  query: string,
  renderOptions: TranslatorRenderOptions = {},
): Promise<void> {
  const [languages, parsed] = await Promise.all([
    loadLanguages(),
    parseQuery(query),
  ]);

  let source = parsed.source || "auto";
  let target = parsed.target || "en";
  let text = parsed.text || "";
  let current = parsed.result || null;
  let symbolsEnabled = true;
  let translationRequestId = 0;

  if (target === "auto") target = "en";

  const embedded = renderOptions.embedded === true;
  const includeExtras = renderOptions.includeExtras !== false;

  container.innerHTML = `
    <div class="aye-translator-page ${embedded ? "aye-translator-page-embedded" : ""}">
      <section class="aye-translator-card">
        <div class="aye-translator-top">
          <select id="aye-translator-source" class="aye-translator-select" aria-label="Source language">
            ${options(languages, source, true)}
          </select>

          <button id="aye-translator-swap" class="aye-translator-swap" type="button" aria-label="Swap languages">⇄</button>

          <select id="aye-translator-target" class="aye-translator-select" aria-label="Target language">
            ${options(languages, target, false)}
          </select>
        </div>

        <div class="aye-translator-assists">
          <button
            id="aye-translator-auto"
            class="aye-translator-assist ${source === "auto" ? "active" : ""}"
            type="button"
            aria-pressed="${source === "auto" ? "true" : "false"}"
          >
            <span class="aye-translator-assist-dot"></span>
            <span id="aye-translator-detected-label">${escapeHtml(
              detectedLabel(current, source),
            )}</span>
          </button>

          <button
            id="aye-translator-symbols"
            class="aye-translator-assist active"
            type="button"
            aria-pressed="true"
          >
            Symbols
          </button>
        </div>

        <div class="aye-translator-grid">
          <div class="aye-translator-pane">
            <textarea id="aye-translator-input" maxlength="1000" placeholder="Enter text">${escapeHtml(text)}</textarea>
            <div class="aye-translator-count"><span id="aye-translator-count">${text.length}</span>/1000</div>
          </div>

          <div class="aye-translator-pane aye-translator-output" id="aye-translator-output">
            ${resultHtml(current)}
          </div>
        </div>

        <div class="aye-translator-actions">
          <button id="aye-translator-button" type="button">Translate</button>
          <button id="aye-translator-copy" class="secondary" type="button">Copy</button>
        </div>
      </section>

      <div id="aye-common-phrases"></div>
      <div id="aye-translate-official-result"></div>
      <div id="aye-people-also-translate"></div>
    </div>
  `;

  const src = container.querySelector<HTMLSelectElement>("#aye-translator-source")!;
  const dst = container.querySelector<HTMLSelectElement>("#aye-translator-target")!;
  const input = container.querySelector<HTMLTextAreaElement>("#aye-translator-input")!;
  const output = container.querySelector<HTMLElement>("#aye-translator-output")!;
  const count = container.querySelector<HTMLElement>("#aye-translator-count")!;
  const common = container.querySelector<HTMLElement>("#aye-common-phrases")!;
  const officialTranslateResult = container.querySelector<HTMLElement>("#aye-translate-official-result")!;
  const peopleAlsoTranslate = container.querySelector<HTMLElement>("#aye-people-also-translate")!;
  const autoButton = container.querySelector<HTMLButtonElement>("#aye-translator-auto")!;
  const symbolsButton = container.querySelector<HTMLButtonElement>("#aye-translator-symbols")!;
  const detected = container.querySelector<HTMLElement>("#aye-translator-detected-label")!;

  const updateAssistState = () => {
    const auto = src.value === "auto";
    autoButton.classList.toggle("active", auto);
    autoButton.setAttribute("aria-pressed", String(auto));
    symbolsButton.classList.toggle("active", symbolsEnabled);
    symbolsButton.setAttribute("aria-pressed", String(symbolsEnabled));
    detected.textContent = detectedLabel(current, src.value);
  };

  const renderPhrases = () => {
    if (!includeExtras) return Promise.resolve();
    return renderCommonPhrase(common, {
      source: src.value === "auto" ? "en" : src.value,
      target: dst.value,
      onSelect: (phrase) => {
        input.value = phrase;
        count.textContent = String(phrase.length);
        void run();
      },
    });
  };

  const run = async () => {
    const value = input.value.trim();
    const requestId = ++translationRequestId;

    // Every translation begins from a clean result state. This prevents the
    // previous detected language/result from leaking into a new word.
    current = null;
    updateAssistState();

    if (!value) {
      output.innerHTML = resultHtml(null);
      return;
    }

    output.innerHTML = `<div class="aye-translator-loading">Translating…</div>`;

    try {
      const result = await translate(
        value,
        src.value,
        dst.value,
        symbolsEnabled,
      );

      // Ignore a slower response belonging to an older input value.
      if (requestId !== translationRequestId) return;
      if (input.value.trim() !== value) return;

      current = result;

      if (
        symbolsEnabled &&
        current.symbols_applied &&
        current.canonical_source_text
      ) {
        input.value = current.canonical_source_text;
        count.textContent = String(input.value.length);
      }

      output.innerHTML = resultHtml(current);
      updateAssistState();
    } catch (error) {
      if (requestId !== translationRequestId) return;
      if (input.value.trim() !== value) return;

      console.error("AyeSearch translator failed:", error);

      current = null;
      output.innerHTML = `
        <div class="aye-translator-error">
          AyeSearch could not confidently identify this text from the current language database.
        </div>
      `;
      updateAssistState();
    }
  };

  let symbolTimer: number | undefined;

  input.addEventListener("input", () => {
    count.textContent = String(input.value.length);

    // Invalidate any request/result from the previous word immediately.
    translationRequestId += 1;
    current = null;
    updateAssistState();

    if (!symbolsEnabled || src.value !== "auto") return;

    if (symbolTimer !== undefined) window.clearTimeout(symbolTimer);

    symbolTimer = window.setTimeout(() => {
      const value = input.value.trim();
      if (value && value.length >= 2 && !value.includes("\n")) {
        void run();
      }
    }, 320);
  });

  container
    .querySelector("#aye-translator-button")
    ?.addEventListener("click", () => void run());

  container
    .querySelector("#aye-translator-copy")
    ?.addEventListener("click", () => {
      if (current?.translation) {
        void navigator.clipboard?.writeText(current.translation);
      }
    });

  container
    .querySelector("#aye-translator-swap")
    ?.addEventListener("click", () => {
      const oldSource =
        src.value === "auto"
          ? current?.source_language || "en"
          : src.value;
      const oldTarget = dst.value;

      src.value = oldTarget;
      dst.value = oldSource;

      if (current) {
        input.value = current.translation;
        count.textContent = String(input.value.length);
        current = null;
        output.innerHTML = resultHtml(null);
      }

      updateAssistState();
      void renderPhrases();
    });

  autoButton.addEventListener("click", () => {
    if (src.value === "auto") {
      src.value = current?.source_language || "en";
    } else {
      src.value = "auto";
    }
    current = null;
    updateAssistState();
    void renderPhrases();
    if (input.value.trim()) void run();
  });

  symbolsButton.addEventListener("click", () => {
    symbolsEnabled = !symbolsEnabled;
    updateAssistState();
    if (input.value.trim()) void run();
  });

  src.addEventListener("change", () => {
    translationRequestId += 1;
    current = null;
    updateAssistState();
    void renderPhrases();
    if (input.value.trim()) void run();
  });

  dst.addEventListener("change", () => {
    translationRequestId += 1;
    current = null;
    updateAssistState();
    void renderPhrases();
    if (input.value.trim()) void run();
  });

  updateAssistState();
  if (includeExtras) {
    renderAyeSearchTranslateOfficialResult(officialTranslateResult, query);
    renderPeopleAlsoTranslate(peopleAlsoTranslate);
    await renderPhrases();
  }

  if (text && !current) await run();
}
